import React, { useState } from 'react';
import { Card, Grid, makeStyles, Typography } from '@material-ui/core';
import { Chip } from 'common';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import LanguageIcon from '@material-ui/icons/Language';
import BuildIcon from '@material-ui/icons/Build';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import SchoolIcon from '@material-ui/icons/School';

import clsx from 'clsx';
import Timeline from './timeline';

const useStyles = makeStyles(() => ({
  contentContainer: {
    display: 'flex',
    padding: '5px 20px 5px 10px',
    textAlign: 'left',
    height: 'calc(100vh - 396px)',
    overflow: 'scroll',
    background: '#fff',
  },
}));

const useCaptionValueStyles = makeStyles(() => ({
  captionValueContainer: {
    borderRadius: 2,
    backgroundColor: '#f7f7f7',
    width: 'calc(100% - 24px)',
    padding: 12,
    marginBottom: 8,
  },
  caption: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: '20px',
    letterSpacing: 0.25,
    textAlign: 'left',
    color: '#9ca8b5',
  },
  value: {
    fontWeight: 500,
    color: '#49515a',
  },
}));

const useSectionStyles = makeStyles(() => ({
  cardContainer: {
    padding: 16,
    borderRadius: 2,
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.11)',
  },
  cardTitle: {
    fontFamily: 'Roboto',
    fontSize: 13,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: '20px',
    letterSpacing: 0.24,
    textAlign: 'left',
    color: '#9ca8b5',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',

    '& > svg': {
      width: 18,
      height: 18,
      paddingRight: 10,
    },
  },
  fieldsContainer: {
    paddingLeft: 26,
  },
}));

const CaptionValueField = ({ caption = '', value = '' }) => {
  const classes = useCaptionValueStyles();

  const formattedValue =
    typeof value === 'string' ? value.trim() || '-' : value;

  return (
    <div className={classes.captionValueContainer}>
      <Typography className={classes.caption}>{caption}</Typography>
      <Typography className={clsx(classes.caption, classes.value)}>
        {formattedValue || '-'}
      </Typography>
    </div>
  );
};

const SectionTitleContainer = ({ title = '', Icon, children }) => {
  const classes = useSectionStyles();

  return (
    <Grid item xs={6}>
      <Card className={classes.cardContainer}>
        <Typography className={classes.cardTitle}>
          <Icon /> {title}
        </Typography>
        {children}
      </Card>
    </Grid>
  );
};

const KeyValueSection = ({ keyValuePairs = [] }) => {
  const classes = useSectionStyles();

  return (
    <div className={classes.fieldsContainer}>
      {keyValuePairs.map(({ key, value }) => (
        <CaptionValueField caption={key} value={value} />
      ))}
    </div>
  );
};

const TagsSection = ({ tags = [] }) => {
  const [showAllSkills, setShowAllSkills] = useState(false);
  const classes = useSectionStyles();

  const tagsToShow = showAllSkills ? tags : tags.slice(0, 10);

  return (
    <div className={classes.fieldsContainer}>
      {tagsToShow.map((tag) => (
        <Chip label={tag} />
      ))}
      {tags.length > 10 ? (
        <Chip
          label={
            showAllSkills
              ? 'Hide More Skills'
              : `+ ${tags.length - 10} More Skills`
          }
          onClick={() => setShowAllSkills((prevState) => !prevState)}
        />
      ) : null}
    </div>
  );
};

const ResumeOverview = ({ json = {} }) => {
  const classes = useStyles();
  const {
    contactInfo = {},
    experience = {},
    contactMethod = {},
    socialLinks = {},
    skills = [],
    education = {},
  } = json;

  const { firstName = '', middleName = '', lastName = '' } = contactInfo;
  const { CurrentCompany = '', CurrentJobRole = '' } = experience;
  const {
    ContactEmailid = [],
    Telephone = [],
    PostalAddress = {},
  } = contactMethod;
  const { Location = {} } = PostalAddress;
  const { facebook = '', twitter = '', linkedin = '', urls = [] } = socialLinks;

  const educationSection = education.EducationSection.map((item) => {
    const major = item.Major ? `Major: ${item.Major}` : '';
    const grade = item.Grade
      ? `${item.Grade}${item.GradeType === 'GPA' ? 'CGPA' : '%'}`
      : '';

    return { ...item, major: `${major}${major && grade ? ' - ' : ''}${grade}` };
  });

  return (
    <>
      <div className={classes.contentContainer}>
        <Grid container spacing={4}>
          <SectionTitleContainer title={'Personal Details'} Icon={PersonIcon}>
            <KeyValueSection
              keyValuePairs={[
                { key: 'First Name', value: firstName },
                { key: 'Middle Name', value: middleName },
                { key: 'Last Name', value: lastName },
                { key: 'Current Job Role', value: CurrentJobRole },
                { key: 'Current Company', value: CurrentCompany },
              ]}
            />
          </SectionTitleContainer>
          <SectionTitleContainer title={'Contact Info'} Icon={PhoneIcon}>
            <KeyValueSection
              keyValuePairs={[
                {
                  key: 'Phone',
                  value: Telephone.join(', '),
                },
                {
                  key: 'Email',
                  value: ContactEmailid.join(', '),
                },
                { key: 'Street Address', value: Location.Area || '' },
                { key: 'City & Region', value: Location.City || '' },
                { key: 'Country', value: Location.Country || '' },
              ]}
            />
          </SectionTitleContainer>
          <SectionTitleContainer
            title={
              'Experience' +
              (experience.TotalExperience
                ? ` (${experience.TotalExperience} years / Avg. Exp/Company: ${experience.AverageExperience})`
                : '')
            }
            Icon={WorkOutlineIcon}
          >
            <Timeline
              list={experience.ExperienceSection}
              keys={{
                title: 'CompanyName',
                subtitle: 'JobTitle',
                caption: 'ExpLocation',
                startYearParent: 'ExpStartDate',
                endYearParent: 'ExpEndDate',
                startYear: 'year',
                endYear: 'year',
                startMonth: 'month',
                endMonth: 'month',
              }}
            />
          </SectionTitleContainer>
          <SectionTitleContainer
            title={
              'Education' +
              (education.HighestEducation
                ? ` (${education.HighestEducation})`
                : '')
            }
            Icon={SchoolIcon}
          >
            <Timeline
              list={educationSection}
              keys={{
                title: 'Degree',
                subtitle: 'Collegename',
                caption: 'Location',
                subcaption: 'major',
                startYearParent: 'EduStartDate',
                endYearParent: 'EduEndDate',
                startYear: 'year',
                endYear: 'year',
                startMonth: 'month',
                endMonth: 'month',
              }}
            />
          </SectionTitleContainer>
          <SectionTitleContainer title={'Skills'} Icon={BuildIcon}>
            <TagsSection tags={skills.map(({ SkillName }) => SkillName)} />
          </SectionTitleContainer>
          <SectionTitleContainer title={'Social Links'} Icon={LanguageIcon}>
            <KeyValueSection
              keyValuePairs={[
                {
                  key: 'Facebook',
                  value: facebook,
                },
                {
                  key: 'Twitter',
                  value: twitter,
                },
                { key: 'Linkedin', value: linkedin },
                { key: 'Others', value: urls.join(', ') },
              ]}
            />
          </SectionTitleContainer>
        </Grid>
      </div>
    </>
  );
};

export default ResumeOverview;
